import { template as _$template } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { use as _$use } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<main class=flight><div class=flight__canvas-host>`);
import { Show } from "solid-js";
import { CockpitPanel } from "./CockpitPanel.tsx";
import { cockpitPanelActive } from "./free-look.ts";
import { ControlsHint } from "./ControlsHint.tsx";
import { FlightSettingsControls } from "./FlightSettingsControls.tsx";
import { FlightStatusOverlay } from "./FlightStatusOverlay.tsx";
import { HudOverlay } from "./HudOverlay.tsx";
import { KillCamFrame } from "./KillCamFrame.tsx";

/**
 * Everything that sits on top of the canvas while flying: the cockpit panel,
 * the HUD, the controls hint, the settings cluster and the status pane.
 *
 * Extracted when `/duel` arrived. A duel is `/fly` with a different source of
 * ticks, and the alternative to sharing this was a second copy of the same
 * eight elements wired to the same eight signals — which the scanner would
 * (rightly) call a duplicated block, and which would drift the moment one
 * route grew a control the other did not.
 *
 * Purely presentational: the two small pieces of chrome state it does own
 * (whether the hint and the settings popover are showing) exist nowhere else
 * and belong to nothing else. Route-specific chrome — the exit link, a
 * debriefing, a duel's outcome banner — comes in as `children`.
 */

export function FlightSurface(props) {
  return (// Holding the primary button is the trigger (`pointer.ts`), so nothing
    // under this root may start a native drag: an image drag or a selection
    // drag would swallow the pointer and leave the gun stuck on.
    (() => {
      var _el$ = _tmpl$(),
        _el$2 = _el$.firstChild;
      _el$.addEventListener("dragstart", event => event.preventDefault());
      var _ref$ = props.containerRef;
      typeof _ref$ === "function" ? _$use(_ref$, _el$2) : props.containerRef = _el$2;
      _$setAttribute(_el$2, "draggable", false);
      _$insert(_el$, _$createComponent(CockpitPanel, {
        get hud() {
          return props.hud;
        },
        get realisticInstruments() {
          return props.cockpitSettings.realisticInstruments;
        },
        get active() {
          return cockpitPanelActive(props.cameraMode, props.cockpitView.freeLook, props.cockpit3d === true);
        },
        get aircraft() {
          return props.aircraftKey;
        },
        get glance() {
          return props.cockpitView.glance;
        },
        get gunFlash() {
          return props.cockpitView.gunFlash;
        },
        get hitMarks() {
          return props.cockpitView.hitMarks;
        },
        get surfaces() {
          return props.cockpitView.surfaces;
        },
        get killCamHole() {
          return props.killCamOpen;
        },
        get killCamPlacement() {
          return props.killCamPlacement ?? null;
        },
        get mode() {
          return props.cockpit3d ? "modelled" : "painted";
        },
        get dashFeed() {
          return props.dashFeed;
        }
      }), null);
      _$insert(_el$, _$createComponent(HudOverlay, {
        get hud() {
          return props.hud;
        },
        get combat() {
          return props.combat;
        },
        get mission() {
          return props.mission;
        },
        get aircraft() {
          return props.aircraft;
        },
        get chips() {
          return props.chips;
        },
        get realismChips() {
          return props.realismChips;
        },
        get messages() {
          return props.messages;
        },
        get cameraLabel() {
          return props.cameraLabel;
        },
        get paused() {
          return props.paused;
        },
        get hideDigitalGauges() {
          return _$memo(() => props.cameraMode === "cockpit")() && props.cockpitSettings.realisticInstruments;
        },
        get hideOutcomeBanner() {
          return props.hideOutcomeBanner;
        },
        get onReturnToTitle() {
          return props.onReturnToTitle;
        }
      }), null);
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return props.killCamOpen;
        },
        get children() {
          return _$createComponent(KillCamFrame, {
            get placement() {
              return props.killCamPlacement ?? null;
            },
            get onMove() {
              return props.onKillCamMove ?? (() => {});
            },
            get onCommit() {
              return props.onKillCamCommit ?? (() => {});
            },
            get onClose() {
              return props.onKillCamClose ?? (() => {});
            }
          });
        }
      }), null);
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return _$memo(() => !!props.hintVisible)() && !props.killCamOpen;
        },
        get children() {
          return _$createComponent(ControlsHint, {
            get onDismiss() {
              return props.onDismissHint;
            }
          });
        }
      }), null);
      _$insert(_el$, _$createComponent(Show, {
        get when() {
          return props.onChangeEffects;
        },
        get children() {
          return _$createComponent(FlightSettingsControls, {
            get audioSettings() {
              return props.audioSettings;
            },
            get onChangeAudio() {
              return props.onChangeAudio;
            },
            get onChangeCockpit() {
              return props.onChangeCockpit;
            },
            get onChangeEffects() {
              return props.onChangeEffects ?? (() => {});
            },
            get onChangeStick() {
              return props.onChangeStick ?? (() => {});
            },
            get onChangeBail() {
              return props.onChangeBail ?? (() => {});
            },
            get gamepad() {
              return props.gamepad;
            },
            get settingsVisible() {
              return props.settingsVisible;
            },
            get onToggleSettingsVisible() {
              return props.onToggleSettings;
            },
            get onDismissSettings() {
              return props.onDismissSettings;
            }
          });
        }
      }), null);
      _$insert(_el$, () => props.children, null);
      _$insert(_el$, _$createComponent(FlightStatusOverlay, {
        get boot() {
          return props.boot;
        }
      }), null);
      return _el$;
    })()
  );
}